/* eslint-disable import/order */
import React, {
  SetStateAction,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Map as ImmutableMap } from "immutable";
import { Button, Collapse, Grid, Toolbar } from "@material-ui/core";
import IvrTalkingLeaderboard from "./IvrTalkingLeaderboard";
import IvrWidgetContainer from "./IvrWidgetContainer";
import { Delta } from "../QueueLeaderboard";
import "./ivr.css";
import {
  ExpandLess,
  ExpandMore,
  TextFormat,
  UnfoldLess,
  UnfoldMore,
} from "@material-ui/icons";
import IvrStatisticsLeaderboard from "./IvrStatisticsLeaderboard";

export interface IvrState {
  ivrId: string;
  state: string;
  direction: string;
  time: string;
  callId: string;
  partyId: string;
  from: {
    name: string;
    phoneNumber: string;
  };
  to: {
    name: string;
    phoneNumber: string;
  };
  endTime: string;
  rowId: string;
  isAir: boolean;
  type?: string;
  name?: string;
}

export interface HistoricalIvr {
  SortKey: string;
  extensionNumber: string;
  id: string;
  name: string;
  isAir: boolean;
  uri?: string;
  site?: {
    name: string;
    id: string;
  };
}

interface Props {
  historicalIvr: ImmutableMap<string, HistoricalIvr>;
  ivrStates: IvrState[];
  ivrDeltas: Delta[];
  mode: string;
  tab: string;
  page: string;
  sidebar: boolean;
  showLeaderboards: boolean | undefined;
  fullscreen: string | undefined;
  setFullscreen: React.Dispatch<SetStateAction<string | undefined>>;
  accessToken: string | undefined;
  excludedAirs: Set<string>
  excludedIvrs: Set<string>
  ivrSortDirection: string | undefined;
  ivrSortField: string | undefined;
  airSortDirection: string | undefined;
  airSortField: string | undefined
}

const IvrHud = React.memo(
  ({
    historicalIvr,
    ivrDeltas,
    ivrStates,
    mode,
    tab,
    page,
    sidebar,
    showLeaderboards,
    fullscreen,
    setFullscreen,
    accessToken,
    excludedAirs,
    excludedIvrs,
    ivrSortDirection,
    ivrSortField,
    airSortDirection,
    airSortField
  }: Props) => {
    const [activeLeaderboard, setActiveLeaderboard] = useState<"talking" | "interactions" | "statistics">("statistics");
    const [gridHeight, setGridHeight] = useState(50);
    const [theme, setTheme] = useState("ag-theme-alpine");
    const [fontSize, setFontSize] = useState<string>("small");
    const [expandLeaderboard, setExpandLeaderboard] = useState(true);

    const smaller = useCallback(() => {
      setGridHeight((prev) => (prev < 20 ? prev : prev - 10));
    }, []);

    const larger = useCallback(() => {
      setGridHeight((prev) => (prev > 80 ? prev : prev + 10));
    }, []);

    const increaseFont = useCallback(() => {
      if (fontSize === "small") {
        setFontSize("medium");
      } else if (fontSize === "medium") {
        setFontSize("large");
      }
    }, [fontSize]);

    const decreaseFont = useCallback(() => {
      if (fontSize === "large") {
        setFontSize("medium");
      } else if (fontSize === "medium") {
        setFontSize("small");
      }
    }, [fontSize]);

    useEffect(() => {
      setTheme(mode === "light" ? "ag-theme-alpine" : "ag-theme-alpine-dark");
    }, [mode]);

    const defaultColDef = useMemo(
      () => ({
        flex: 1,
        minWidth: 100,
        sortable: false,
        cellClass: " notarnslate ",
        resizable: true,
        filter: true,
        cellClassRules: {
          small: () => fontSize === "small",
          medium: () => fontSize === "medium",
          large: () => fontSize === "large",
        },
      }),
      [fontSize]
    );

    useEffect(() => {
      if (historicalIvr.size) {
        console.log("Historical IVR", Array.from(historicalIvr.values()));
      }
    }, [historicalIvr]);
    useEffect(() => {
      if (ivrDeltas.length) {
        console.log("IVR Delta", ivrDeltas);
      }
    }, [ivrDeltas]);
    useEffect(() => {
      if (ivrStates.length) {
        console.log("IVR States", ivrStates);
      }
    }, [ivrStates]);
    return (
      <div
        className={`ivr-hud ${
          mode === "light" ? "light-ivr-hud" : "dark-ivr-hud"
        } ${sidebar ? "sidebar-open" : ""}`}
        style={tab === "air" || tab === "ivr" ? {} : { display: "none" }}
      >
        {showLeaderboards && (
          <div className="ivr-leaderboard-container">
            <Toolbar
              disableGutters
              variant="dense"
              style={{
                minHeight: "50px",
                backgroundColor: mode === "dark" ? "#181818" : "#0684bd",
                borderRadius: "5px 5px 0px 0px",
              }}
            >
              <Grid container justifyContent="space-between">
                <Grid item xs={6}>
                  <Button
                    id="ivr-leaderboard-expand"
                    variant="text"
                    disableRipple
                    style={{
                      backgroundColor: "transparent",
                      color: "#fff",
                      textTransform: "none",
                      paddingTop: "0.3rem",
                    }}
                    onClick={() => setExpandLeaderboard(!expandLeaderboard)}
                    startIcon={
                      expandLeaderboard ? (
                        <ExpandLess style={{ color: "white" }} />
                      ) : (
                        <ExpandMore style={{ color: "white" }} />
                      )
                    }
                  >
                    Leaderboard
                  </Button>
                </Grid>
                {expandLeaderboard && (
                  <Grid
                    container
                    item
                    xs={6}
                    alignItems="center"
                    direction="row-reverse"
                    style={{ paddingTop: "0.3rem" }}
                  >
                    <Grid container item xs={12} justifyContent="flex-end">
                      <button
                        className="user-leaderboard-header-button"
                        type="button"
                        onClick={larger}
                      >
                        <UnfoldMore style={{ color: "white" }} />
                      </button>
                      <button
                        className="user-leaderboard-header-button"
                        type="button"
                        onClick={smaller}
                      >
                        <UnfoldLess style={{ color: "white" }} />
                      </button>
                      <button
                        onClick={increaseFont}
                        className="user-leaderboard-header-button"
                        type="button"
                      >
                        <TextFormat
                          style={{ fontSize: "1.8rem", color: "white" }}
                        />
                      </button>
                      <button
                        onClick={decreaseFont}
                        className="user-leaderboard-header-button"
                        type="button"
                      >
                        <TextFormat
                          fontSize="small"
                          style={{ color: "white" }}
                        />
                      </button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Toolbar>
            <Collapse in={expandLeaderboard} timeout="auto">
                
              <IvrTalkingLeaderboard
                historicalIvr={historicalIvr}
                ivrDeltas={ivrDeltas}
                ivrStates={ivrStates}
                fontSize={fontSize}
                defaultColDef={defaultColDef}
                activeLeaderboard={activeLeaderboard}
                theme={theme}
                gridHeight={gridHeight}
                tab={tab}
                excludedIvrs={[...excludedAirs, ...excludedIvrs]}
              />

              <IvrStatisticsLeaderboard
                historicalIvr={historicalIvr}
                ivrDeltas={ivrDeltas}
                ivrStates={ivrStates}
                fontSize={fontSize}
                defaultColDef={defaultColDef}
                activeLeaderboard={activeLeaderboard}
                theme={theme}
                gridHeight={gridHeight}
                tab={tab}
                excludedIvrs={[...excludedAirs, ...excludedIvrs]}
              />

              <div 
              style={{background: mode === 'light' ? "#0684bd" : "rgb(24, 24, 24)"}}
              className={`ivr-leaderboard-toolbar ${mode === 'light' ? "ivr-light-mode" : "ivr-dark-mode"}`}>
                <button
                  type="button"
                  className={`ivr-leaderboard-toggle ${mode} ${activeLeaderboard === "statistics" ? "active" : ""}`}
                  onClick={() => setActiveLeaderboard("statistics")}>
                  Summary
                </button>
                <button
                  type="button"
                  className={`ivr-leaderboard-toggle ${mode} ${activeLeaderboard === "talking" ? "active" : ""}`}
                  onClick={() => setActiveLeaderboard("talking")}>
                  In Progress
                </button>
                <button
                  type="button"
                  className={`ivr-leaderboard-toggle ${mode} ${activeLeaderboard === "interactions" ? "active" : ""}`}
                  onClick={() => setActiveLeaderboard("interactions")}>
                  Interactions
                </button>
              </div>
            </Collapse>
          </div>
        )}
        <IvrWidgetContainer
          historicalIvr={historicalIvr}
          ivrDeltas={ivrDeltas}
          ivrStates={ivrStates}
          tab={tab}
          mode={mode}
          setFullscreen={setFullscreen}
          fullscreen={fullscreen}
          sidebar={sidebar}
          accessToken={accessToken}
          excludedIvrs={[...excludedAirs, ...excludedIvrs]}
          airSortDirection={airSortDirection}
          airSortField={airSortField}
          ivrSortDirection={ivrSortDirection}
          ivrSortField={ivrSortField}
        />
      </div>
    );
  }
);

export default IvrHud;
